import type { ReactNode } from 'react';

import { css, styled } from 'styled-components';

import CheckboxIcon from '../../../../assets/icons/checkbox.svg?react';

/* Our Checkbox component must be wrapped by a label element. 
The label element will be solely responsible for triggering the onChange callback of the Checkbox
since the native checkbox element will be visually hidden. */
const LabelSC = styled.label`
  display: flex;
  user-select: none;
  cursor: pointer;
`;

/* Hide checkbox visually but remain accessible to screen readers.
Inspired by https://getbootstrap.com/docs/4.3/getting-started/accessibility/ */
const SROnlyCheckbox = styled.input.attrs({ type: 'checkbox' })`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const checkboxColorsGreen = css`
  color: ${(props) => props.theme.colors.emeraldGreen[100]};
`;

const checkboxColorsAqua = css`
  color: ${(props) => props.theme.colors.tiffanyGreen[100]};
`;

const checkboxStrokeGreen = css`
  stroke: ${(props) => props.theme.colors.emeraldGreen[100]};
`;

const checkboxStrokeAqua = css`
  stroke: ${(props) => props.theme.colors.tiffanyGreen[100]};
`;

const CheckboxIconSC = styled(CheckboxIcon)<{ $colorVariant?: 'green' | 'aqua' }>`
  ${(props) => {
    switch (props.$colorVariant) {
      case 'aqua':
        return checkboxStrokeAqua;
      default:
        return checkboxStrokeGreen;
    }
  }}
  width: 0.85rem;
`;

const CustomCheckbox = styled.span<{ $checked: boolean; $colorVariant?: 'green' | 'aqua' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
  ${(props) => {
    switch (props.$colorVariant) {
      case 'aqua':
        return checkboxColorsAqua;
      default:
        return checkboxColorsGreen;
    }
  }}
  border: 0.125rem solid;
  border-radius: 0.375rem;
  margin-inline-end: 1.125rem;

  ${SROnlyCheckbox}:focus + & {
    box-shadow: 0 0 0 0.1875rem ${(props) => props.theme.colors.emeraldGreen[50]};
  }

  ${CheckboxIconSC} {
    visibility: ${(props) => (props.$checked ? 'visible' : 'hidden')};
  }
`;

const LabelText = styled.span<{ $labelColor?: string }>`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${(props) => props.$labelColor ?? props.theme.colors.oxfordBlue[80]};
  line-height: 1.5rem;
`;

export type CheckboxProps = {
  checked: boolean;
  children: ReactNode;
  colorVariant?: 'green' | 'aqua';
  'data-test-id'?: string;
  labelColor?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export const Checkbox = ({
  checked,
  children,
  colorVariant,
  'data-test-id': dataTestId,
  labelColor,
  onChange,
}: CheckboxProps) => {
  return (
    <LabelSC data-test-id={dataTestId}>
      <SROnlyCheckbox checked={checked} onChange={onChange} />
      <CustomCheckbox $checked={checked} $colorVariant={colorVariant}>
        <CheckboxIconSC $colorVariant={colorVariant} />
      </CustomCheckbox>
      <LabelText $labelColor={labelColor}>{children}</LabelText>
    </LabelSC>
  );
};
